// client/src/components/vendas/VendasPage.js - PADRONIZADO
import React, { Fragment, useState, useMemo, useCallback, useEffect } from 'react';
import { useVendas, useMutateVendas, useVendasEstatisticasBasicas } from '../../hooks/useVendas';
import { useVendaFormQuickData } from '../../hooks/useVendaFormData';
import { useTenantI18n } from '../../hooks/useAccounts';
import { formatCurrency } from '../../utils/formatters';
import VendaForm from './VendaForm';
import VendaRow from './VendaRow';
import FiltroVendas from './FiltroVendas';
import PDVModal from './PDVModal';
import {
    Filter as FilterIcon,
    Loader2,
    RefreshCcw,
    TrendingUp,
    XCircle,
    ShoppingCart,
    DollarSign,
    Users,
    Clock,
    SquarePen,
    Trash2,
    CheckCircle2,
    AlertTriangle,
    PlusCircle,
} from 'lucide-react';
import Pagination from '../common/Pagination';
import PageHeader from '../layout/PageHeader';
import FabButton from '../common/FabButton';
import ActionsDrawer from '../common/ActionsDrawer';

const Vendas = () => {
    // ✅ ACHADO: KPI fixo em BRL, sem ler configuracoesGerais.i18n da conta.
    const { currency, locale } = useTenantI18n();
    const [isFormVisible, setIsFormVisible] = useState(false);
    const [showFilters, setShowFilters] = useState(false);
    const [showPDV, setShowPDV] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [activeFilters, setActiveFilters] = useState({});
    const [currentVenda, setCurrentVenda] = useState(null);
    const [selectedVenda, setSelectedVenda] = useState(null);
    const vendasPerPage = 10;

    const queryParams = useMemo(() => ({
        page: currentPage, 
        limit: vendasPerPage, 
        ...activeFilters
    }), [currentPage, activeFilters]);

    const { data: vendasData, isLoading, refetch, error } = useVendas(queryParams);
    const { deleteVenda, updateVenda } = useMutateVendas();
    const isDeleting = deleteVenda.isPending;
    const isFinalizando = updateVenda.isPending;
    
    const estatisticas = useVendasEstatisticasBasicas('hoje');

    const { isLoading: loadingFormData } = useVendaFormQuickData();

    const vendas = vendasData?.data || [];
    const totalVendas = vendasData?.count || 0;
    const pagination = vendasData?.pagination || {};
    
    const handleApplyFilters = useCallback((filters) => { 
        setCurrentPage(1); 
        setActiveFilters(filters); 
    }, []);

    const handleAdicionarClick = useCallback(() => { 
        setCurrentVenda(null); 
        setIsFormVisible(true); 
    }, []);

    const handlePDVClick = useCallback(() => {
        setShowPDV(true);
    }, []);

    const handleCloseForm = useCallback(() => { 
        setIsFormVisible(false); 
        setCurrentVenda(null); 
    }, []);

    const handleClosePDV = useCallback(() => {
        setShowPDV(false);
    }, []);

    const handleDelete = useCallback((vendaId) => {
        if (window.confirm('Tem certeza que deseja cancelar esta venda?')) {
            deleteVenda.mutate(vendaId);
        }
        setSelectedVenda(null);
    }, [deleteVenda]);

    const handleFinalizar = useCallback((venda) => {
        if (window.confirm('Confirmar a finalização desta venda?')) {
            updateVenda.mutate({ id: venda._id, vendaData: { statusVenda: 'Finalizada' } });
        }
        setSelectedVenda(null);
    }, [updateVenda]);

    const handleSaveSuccess = useCallback(() => {
        handleCloseForm();
        handleClosePDV();
        refetch();
    }, [handleCloseForm, handleClosePDV, refetch]);

    const handleEditClick = useCallback((venda) => {
        setCurrentVenda(venda);
        setIsFormVisible(true);
        setSelectedVenda(null);
    }, []);

    if (error) {
        // .error-state não tinha CSS em lugar nenhum do projeto — mesmo
        // problema já corrigido em OfertasPage.js/PedidosPage.js.
        return (
            <div className="page-management-container">
                <div className="table-error-state">
                    <AlertTriangle size={48} />
                    <h2>Erro ao carregar vendas</h2>
                    <p>{error.message || 'Ocorreu um erro ao buscar as vendas.'}</p>
                    <button
                        onClick={() => refetch()}
                        className="action-button action-button-primary"
                    >
                        <RefreshCcw size={18} className="icon"/>
                        Tentar Novamente
                    </button>
                </div>
            </div>
        );
    }

    return (
        <Fragment>
            {/* MODAL PDV COM Z-INDEX ALTO */}
            {showPDV && (
                <PDVModal 
                    onClose={handleClosePDV}
                    onSaveSuccess={handleSaveSuccess}
                    loading={loadingFormData}
                />
            )}

            <div className="page-management-container">
                <PageHeader
                    icon={<TrendingUp />}
                    title="Sistema de Vendas"
                    actions={<>
                        {!isFormVisible && !showPDV && (
                            <>
                                <button
                                    onClick={() => setShowFilters(p => !p)}
                                    className={`action-button ${showFilters ? 'action-button-primary' : 'action-button-secondary'}`}
                                    disabled={isLoading}
                                >
                                    <FilterIcon size={18} className="icon"/>
                                    {showFilters ? 'Ocultar Filtros' : 'Filtros'}
                                </button>
                                <button
                                    onClick={handlePDVClick}
                                    className="action-button action-button-secondary"
                                    disabled={isLoading || loadingFormData}
                                >
                                    <ShoppingCart size={18} className="icon"/>
                                    Abrir PDV
                                </button>
                            </>
                        )}
                        {isFormVisible && (
                            <button onClick={handleCloseForm} className="action-button action-button-secondary" disabled={isLoading}>
                                <XCircle size={18} className="icon"/> Fechar
                            </button>
                        )}
                    </>}
                />

                {isFormVisible && (
                    <div className="content-panel">
                        <VendaForm 
                            key={currentVenda?._id || 'new'}
                            onCloseForm={handleCloseForm}
                            onSaveSuccess={handleSaveSuccess}
                            current={currentVenda}
                        />
                    </div>
                )}

                {showFilters && !isFormVisible && !showPDV && (
                    <div className="content-panel">
                        <h3 className="panel-title">Filtros</h3>
                        <FiltroVendas onApplyFilters={handleApplyFilters} loading={isLoading} />
                    </div>
                )}
            </div>

            {!isFormVisible && !showPDV && (
                <Fragment>
                    {/* ✅ CORREÇÃO: USANDO dashboard-grid PADRONIZADO DO SISTEMA */}
                    <div className="dashboard-grid" style={{ marginTop: '1.5rem' }}>
                        <div className="kpi-card">
                            <div className="kpi-card__icon">
                                <ShoppingCart size={28} />
                            </div>
                            <h5 className="kpi-card__title">Total de Vendas (Hoje)</h5>
                            <p className="kpi-card__value">
                                {estatisticas.totalVendas}
                            </p>
                            <div className="kpi-trend positive">
                                +12% em relação a ontem
                            </div>
                        </div>
                        
                        <div className="kpi-card">
                            <div className="kpi-card__icon">
                                <DollarSign size={28} />
                            </div>
                            <h5 className="kpi-card__title">Faturamento (Hoje)</h5>
                            <p className="kpi-card__value">
                                {formatCurrency(estatisticas.faturamentoTotal || 0, currency, locale)}
                            </p>
                            <div className="kpi-trend positive">
                                +8% em relação a ontem
                            </div>
                        </div>
                        
                        <div className="kpi-card">
                            <div className="kpi-card__icon">
                                <Users size={28} />
                            </div>
                            <h5 className="kpi-card__title">Vendas Finalizadas</h5>
                            <p className="kpi-card__value">
                                {estatisticas.vendasFinalizadas}
                            </p>
                            <div className="kpi-trend">
                                Taxa de conversão: {estatisticas.taxaConversao?.toFixed(1) || '0.0'}%
                            </div>
                        </div>
                        
                        <div className="kpi-card">
                            <div className="kpi-card__icon">
                                <Clock size={28} />
                            </div>
                            <h5 className="kpi-card__title">Em Aberto</h5>
                            <p className="kpi-card__value">
                                {estatisticas.vendasPendentes}
                            </p>
                            <div className="kpi-trend warning">
                                Necessita atenção
                            </div>
                        </div>
                    </div>

                    <div className="table-container">
                        <div className="table-wrapper">
                            {isLoading && vendas.length === 0 ? (
                                <div className="table-loading-state">
                                    <Loader2 size={24} className="animate-spin" />
                                </div>
                            ) : vendas.length > 0 ? (
                                <table className="styled-table">
                                    <thead>
                                        <tr>
                                            <th>Cliente</th>
                                            <th>Data/Hora</th>
                                            <th>Itens</th>
                                            <th>Total</th>
                                            <th>Status Venda</th>
                                            <th>Status Pagamento</th>
                                            <th className="text-right">Ações</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {vendas.map(venda => (
                                            <VendaRow
                                                key={venda._id}
                                                venda={venda}
                                                onOpenActions={setSelectedVenda}
                                            />
                                        ))}
                                    </tbody>
                                </table>
                            ) : (
                                <div className="table-empty-state">
                                    <ShoppingCart size={64} className="text-gray-300 mb-4" />
                                    <h3>Nenhuma venda encontrada</h3>
                                    <p>
                                        {Object.keys(activeFilters).length > 0
                                            ? 'Tente ajustar os filtros para ver mais resultados.'
                                            : 'Comece registrando sua primeira venda.'}
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                </Fragment>
            )}
            
            {!isFormVisible && !showPDV && totalVendas > vendasPerPage && !isLoading && (
                <Pagination
                    {...pagination}
                    totalItems={totalVendas}
                    onPageChange={setCurrentPage}
                    currentPage={currentPage}
                />
            )}

            <FabButton onClick={handleAdicionarClick} title="Adicionar Venda" visible={!isFormVisible && !showPDV} disabled={isLoading} icon={<ShoppingCart size={26} className="icon"/>} />

            <ActionsDrawer
                open={!!selectedVenda}
                onClose={() => setSelectedVenda(null)}
                title={selectedVenda?.cliente?.nome || 'Venda'}
                subtitle={selectedVenda?.dataFormatada}
                actions={selectedVenda ? [
                    {
                        key: 'editar',
                        label: 'Editar Venda',
                        icon: <SquarePen size={20} className="icon" />,
                        onClick: () => handleEditClick(selectedVenda),
                    },
                    ...(selectedVenda.statusVenda === 'Aberta' ? [{
                        key: 'finalizar',
                        label: 'Finalizar Venda',
                        icon: <CheckCircle2 size={20} className="icon" />,
                        onClick: () => handleFinalizar(selectedVenda),
                        success: true,
                        loading: isFinalizando,
                    }] : []),
                    ...(selectedVenda.statusVenda !== 'Cancelada' ? [{
                        key: 'cancelar',
                        label: 'Cancelar Venda',
                        icon: <Trash2 size={20} className="icon" />,
                        onClick: () => handleDelete(selectedVenda._id),
                        destructive: true,
                        loading: isDeleting,
                    }] : []),
                ] : []}
            />
        </Fragment>
    );
};

export default Vendas;